import { all, put, takeEvery, fork, call, select } from "redux-saga/effects";
import { apiURL, withWsDataset } from "../actions/helpers";
import axios from "axios";
import { reversePortfolioHashMap } from "../../components/WealthSeriesPortfolioPage/components/WealthSeriesPortfolioTickers/tickerData";
export const init_portfolio = {
  portfolio_id: "",
  name: "",
  description: "",
  tier: "",
  strategy: "",
  theme_weightage: {},
  rebalance_id: "",
  validity: "",
  created_at: "",
  updated_at: "",
  allocations: [],
  growth: [],
  adjustments: [],
  sparkline: [], // Added sparkline to the initial portfolio structure
};
// Initial State
const INIT_STATE = {
  portfolio: init_portfolio,
  loading: true,
  error: null,
  lastUpdated: {}, // Track when each portfolio was last fetched
  cacheTTL: 3600000 * 3, // Cache time-to-live in milliseconds (3 hours)
};

// Action Types
export const PORTFOLIO_LOAD = "PORTFOLIO/LOAD"; // Restored for individual portfolio loading
export const PORTFOLIO_LOAD_SUCCESS = "PORTFOLIO/LOAD/SUCCESS";
export const PORTFOLIO_LOAD_FAILURE = "PORTFOLIO/LOAD/FAILURE";
export const PORTFOLIO_ADJUSTMENTS_LOAD_SUCCESS =
  "PORTFOLIO/ADJUSTMENTS/LOAD/SUCCESS";
export const PORTFOLIOS_BY_TIER_LOAD = "PORTFOLIOS/BY_TIER/LOAD";

// Action Creators
// Restored for individual portfolio loading
export const portfolioLoad = (
  portfolioId,
  addSparkline = false,
  forceRefresh = false,
) => {
  return {
    type: PORTFOLIO_LOAD,
    portfolioId,
    addSparkline,
    forceRefresh,
  };
};

export const portfolioLoadSuccess = (data, sparklineData = []) => ({
  type: PORTFOLIO_LOAD_SUCCESS,
  data,
  sparklineData,
});

export const portfolioLoadFailure = (error) => ({
  type: PORTFOLIO_LOAD_FAILURE,
  error,
});

export const portfolioAdjustmentsLoadSuccess = (adjustments, id) => ({
  type: PORTFOLIO_ADJUSTMENTS_LOAD_SUCCESS,
  adjustments,
  id,
});

// Action creator for fetching portfolios by tier with cache control
export const portfoliosByTierLoad = (
  tier,
  addSparkline = true,
  forceRefresh = false,
) => ({
  type: PORTFOLIOS_BY_TIER_LOAD,
  tier,
  addSparkline,
  forceRefresh,
});

// Selectors
const getPortfolioState = (state) => state.wealthPortfolio;

// Check if a portfolio is in cache and still fresh
function* isPortfolioCached(portfolioId) {
  const state = yield select(getPortfolioState);
  const portfolioName = reversePortfolioHashMap[portfolioId];

  if (!portfolioName) {
    return false; // Portfolio not in state
  }

  if (!state[portfolioName]) {
    return false; // Portfolio data not in state
  }

  const lastUpdated = state.lastUpdated[portfolioId];
  if (!lastUpdated) {
    return false; // No timestamp, consider it not cached
  }

  const now = Date.now();
  const isFresh = now - lastUpdated < state.cacheTTL;

  // Also check if adjustments are present
  const hasAdjustments =
    state[portfolioName].adjustments &&
    state[portfolioName].adjustments.length > 0;

  return isFresh && hasAdjustments;
}

// Restored individual portfolio saga with cache check
function* fetchPortfolio(action) {
  const { portfolioId, addSparkline = false, forceRefresh = false } = action;

  // Check cache unless force refresh is requested
  if (!forceRefresh) {
    const isCached = yield call(isPortfolioCached, portfolioId);
    if (isCached) {
      // If cached and fresh, skip fetching
      return;
    }
  }

  try {
    // Let backend determine if auto portfolio should be used
    const { data } = yield axios.get(
      withWsDataset(`${apiURL}/wealth-series/portfolios/${portfolioId}`),
      { withCredentials: true },
    );

    const adjustmentsURL = withWsDataset(
      `${apiURL}/wealth-series/portfolios/${portfolioId}/adjustments`,
    );
    const adjustmentsResponse = yield call(axios.get, adjustmentsURL, {
      withCredentials: true,
    });

    let sparklineData = [];
    if (addSparkline) {
      const sparklineResponse = yield axios.get(
        withWsDataset(
          `${apiURL}/wealth-series/portfolios/${portfolioId}/performance?period=1y`,
        ),
        { withCredentials: true },
      );
      sparklineData = sparklineResponse?.data?.performance?.records || [];
    }

    // Update cache timestamp
    yield put({
      type: "PORTFOLIO/UPDATE_CACHE_TIMESTAMP",
      portfolioId,
      timestamp: Date.now(),
    });

    yield put(
      portfolioLoadSuccess(
        {
          portfolio: data,
          loading: false,
        },
        sparklineData,
      ),
    );

    yield put(
      portfolioAdjustmentsLoadSuccess(adjustmentsResponse.data, portfolioId),
    );
  } catch (error) {
    yield put(portfolioLoadFailure(error));
  }
}

// Saga for fetching portfolios by tier with cache check
function* fetchPortfoliosByTier(action) {
  const { tier, addSparkline = true, forceRefresh = false } = action;
  try {
    // Let backend determine if auto portfolios should be used based on user's session
    const { data } = yield axios.get(
      withWsDataset(`${apiURL}/wealth-series/portfolios?tier=${tier}`),
      { withCredentials: true },
    );

    // Filter out portfolios that are already cached (unless forceRefresh is true)
    const portfoliosToFetch = [];
    if (!forceRefresh) {
      for (const portfolio of data) {
        const portfolioId = portfolio.portfolio_id;
        const isCached = yield call(isPortfolioCached, portfolioId);

        if (!isCached) {
          portfoliosToFetch.push(portfolio);
        } else {
          // For cached portfolios, dispatch portfolio data from cache
          const state = yield select(getPortfolioState);
          const portfolioName = reversePortfolioHashMap[portfolioId];

          if (portfolioName && state[portfolioName]) {
            // We have the complete portfolio data in cache, use it
            yield put(
              portfolioLoadSuccess(
                {
                  portfolio,
                  loading: false,
                },
                state[portfolioName].sparkline || [],
              ),
            );

            // Re-dispatch the cached adjustments data
            if (state[portfolioName].adjustments) {
              yield put(
                portfolioAdjustmentsLoadSuccess(
                  state[portfolioName].adjustments,
                  portfolioId,
                ),
              );
            }
          }
        }
      }
    } else {
      portfoliosToFetch.push(...data);
    }

    // Process non-cached portfolios
    if (portfoliosToFetch.length > 0) {
      // Create arrays of promises for batch fetching
      const adjustmentsPromises = portfoliosToFetch.map((portfolio) =>
        axios.get(
          withWsDataset(
            `${apiURL}/wealth-series/portfolios/${portfolio.portfolio_id}/adjustments`,
          ),
          {
            withCredentials: true,
          },
        ),
      );

      const sparklinePromises = addSparkline
        ? portfoliosToFetch.map((portfolio) =>
            axios.get(
              withWsDataset(
                `${apiURL}/wealth-series/portfolios/${portfolio.portfolio_id}/performance?period=1y`,
              ),
              { withCredentials: true },
            ),
          )
        : [];

      // Execute all requests in parallel
      const adjustmentsResults = yield Promise.all(adjustmentsPromises);
      const sparklineResults = addSparkline
        ? yield Promise.all(sparklinePromises)
        : [];

      // Process the results
      for (let i = 0; i < portfoliosToFetch.length; i++) {
        const portfolio = portfoliosToFetch[i];
        const adjustmentsData = adjustmentsResults[i].data;
        const sparklineData = addSparkline
          ? sparklineResults[i]?.data?.performance?.records || []
          : [];

        // Update cache timestamp
        yield put({
          type: "PORTFOLIO/UPDATE_CACHE_TIMESTAMP",
          portfolioId: portfolio.portfolio_id,
          timestamp: Date.now(),
        });

        // Dispatch success actions
        yield put(
          portfolioLoadSuccess(
            {
              portfolio,
              loading: false,
            },
            sparklineData,
          ),
        );

        yield put(
          portfolioAdjustmentsLoadSuccess(
            adjustmentsData,
            portfolio.portfolio_id,
          ),
        );
      }
    }
  } catch (error) {
    yield put(portfolioLoadFailure(error));
  }
}

function* listenPortfolioLoad() {
  yield takeEvery(PORTFOLIO_LOAD, fetchPortfolio);
}

function* listenPortfoliosByTierLoad() {
  yield takeEvery(PORTFOLIOS_BY_TIER_LOAD, fetchPortfoliosByTier);
}

// Root Saga
export function* saga() {
  yield all([fork(listenPortfolioLoad), fork(listenPortfoliosByTierLoad)]);
}

// Reducer
const reducer = (state = INIT_STATE, action) => {
  let id, portfolioName, newState, portfolioId, portName; // Declare variables outside the switch statement
  switch (action.type) {
    case PORTFOLIO_LOAD_SUCCESS:
      id = action.data.portfolio.portfolio_id;
      portfolioName = reversePortfolioHashMap[id];
      newState = {
        ...state,
        loading: action.data.loading,
        error: null,
      };
      if (portfolioName && !action.data.loading) {
        newState[portfolioName] = {
          ...action.data.portfolio,
          sparkline: action.sparklineData, // Add sparkline data to the portfolio
        };
      }
      return newState;
    case PORTFOLIO_ADJUSTMENTS_LOAD_SUCCESS:
      portfolioId = action.id;
      portName = reversePortfolioHashMap[portfolioId];
      return {
        ...state,
        [portName]: {
          ...state[portName],
          adjustments: action.adjustments,
        },
      };
    case PORTFOLIO_LOAD_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    case "PORTFOLIO/UPDATE_CACHE_TIMESTAMP":
      return {
        ...state,
        lastUpdated: {
          ...state.lastUpdated,
          [action.portfolioId]: action.timestamp,
        },
      };
    default:
      return state;
  }
};

export default reducer;
